import { STORAGE_KEYS } from "@/config/constants";
import { safeLocalStorage, safeSessionStorage } from "@/lib/storage/safe-local-storage";
import { safeRandomUUID } from "@/lib/utils/uuid";

export function getDeviceId(): string {
  const ls = safeLocalStorage();
  if (!ls) return "";

  let deviceId = ls.getItem(STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = safeRandomUUID();
    try { ls.setItem(STORAGE_KEYS.DEVICE_ID, deviceId); } catch { /* quota / private mode */ }
  }

  return deviceId;
}

export function getLastUsedAuthType(): string | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try { return ls.getItem(STORAGE_KEYS.LAST_USED_AUTH_TYPE); } catch { return null; }
}

export function setLastUsedAuthType(type: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try { ls.setItem(STORAGE_KEYS.LAST_USED_AUTH_TYPE, type); } catch { /* ignore */ }
}

/**
 * Mark that 2FA verification is pending. The actual pre-2FA JWT is stored
 * server-side in the `spk_pre_auth` httpOnly cookie — we only keep a
 * boolean flag here so the client knows to show the 2FA screen instead
 * of attempting session hydration.
 */
export function setPendingTwoFactorToken(_token: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try { ls.setItem(STORAGE_KEYS.TWO_FACTOR_PENDING, "true"); } catch { /* ignore */ }
}

export function getPendingTwoFactorToken(): string {
  const ls = safeLocalStorage();
  if (!ls) return "";
  try { return ls.getItem(STORAGE_KEYS.TWO_FACTOR_PENDING) || ""; } catch { return ""; }
}

export function clearPendingTwoFactorToken(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try { ls.removeItem(STORAGE_KEYS.TWO_FACTOR_PENDING); } catch { /* ignore */ }
}

export function hasPendingTwoFactorToken(): boolean {
  return Boolean(getPendingTwoFactorToken());
}

export function clearLegacyAuthStorage(): void {
  const ls = safeLocalStorage();
  if (ls) {
    for (const key of [
      STORAGE_KEYS.ACCESS_TOKEN,
      STORAGE_KEYS.REFRESH_TOKEN,
      STORAGE_KEYS.IS_AUTHENTICATED,
      STORAGE_KEYS.SSO_ACTION,
      STORAGE_KEYS.INVITE_TOKEN,
      STORAGE_KEYS.EVENT_LOGOUT,
      STORAGE_KEYS.INTERCOM_USER_HASH,
    ]) {
      try { ls.removeItem(key); } catch { /* ignore */ }
    }
  }
  clearPendingTwoFactorToken();
  const ss = safeSessionStorage();
  if (ss) {
    try { ss.removeItem("oauth_nonce"); } catch { /* ignore */ }
    try { ss.removeItem("onboarding.pendingComplete"); } catch { /* ignore */ }
  }
}

export function triggerCrossTabLogoutFallback(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try { ls.setItem(STORAGE_KEYS.EVENT_LOGOUT, Date.now().toString()); } catch { /* ignore */ }
}
